import { useEffect, useMemo } from "react";

import { useAddonsState } from "app/contexts/AddonStateContext/AddonStateContext";
import { AddOnType, historyHookPreselect } from "app/contexts/AddonStateContext/const";
import { useNoTouchPurchaseContext } from "app/contexts/NoTouchPurchaseContext/NoTouchPurchaseContext";
import { HD_BANNER_CTA_SELECTOR } from "app/marketing/components/common/banners/HDBanner/HDBanner";
import { openHooksModalV5 } from "app/services/ModalService";
import { allTrackers } from "app/services/track/track";

export const HD_BANNER_PRESELECTED_PERIOD = 15;

export interface IHDBannerCtaState {
    canPurchaseAddon: boolean;
    canUpgradeAddon: boolean;
    hasMaxedOutAddon: boolean;
    isLoading: boolean;
}

interface IUseHDBannerCtaParams {
    trackingName: string;
    period?: number;
}

type HdBannerCtaEventHandler = (event: MouseEvent) => void;

const getHdBannerCtaTrackingSuffix = (ctaState: IHDBannerCtaState): string => {
    if (ctaState.canPurchaseAddon) {
        return "Unlock";
    }

    if (ctaState.canUpgradeAddon) {
        return "Upgrade";
    }

    return "Talk to us";
};

const getHdBannerCtaTrackingName = (trackingName: string, ctaState: IHDBannerCtaState): string =>
    `${trackingName}/${getHdBannerCtaTrackingSuffix(ctaState)}`;

/*
 * Highcharts tooltips all share `.hd-banner-cta`, and GenericGraph mounts this hook per metric.
 * One document listener plus last-subscriber-wins keeps a single click from opening purchase N times.
 */
const clickHandlers = new Set<HdBannerCtaEventHandler>();

const handleDocumentClick = (event: MouseEvent): void => {
    [...clickHandlers].at(-1)?.(event);
};

const subscribeHdBannerCtaClick = (handler: HdBannerCtaEventHandler): (() => void) => {
    clickHandlers.add(handler);
    if (clickHandlers.size === 1) {
        document.addEventListener("click", handleDocumentClick);
    }

    return () => {
        clickHandlers.delete(handler);
        if (clickHandlers.size === 0) {
            document.removeEventListener("click", handleDocumentClick);
        }
    };
};

const hoverHandlers = new Set<HdBannerCtaEventHandler>();

const handleDocumentMouseOver = (event: MouseEvent): void => {
    [...hoverHandlers].at(-1)?.(event);
};

const subscribeHdBannerCtaHover = (handler: HdBannerCtaEventHandler): (() => void) => {
    hoverHandlers.add(handler);
    if (hoverHandlers.size === 1) {
        document.addEventListener("mouseover", handleDocumentMouseOver);
    }

    return () => {
        hoverHandlers.delete(handler);
        if (hoverHandlers.size === 0) {
            document.removeEventListener("mouseover", handleDocumentMouseOver);
        }
    };
};

/**
 * Resolves which CTA the historical data banner should offer, and handles its click: the one click
 * purchase flow while there is something left to buy, contact sales once the add-on is maxed out.
 * Chart tooltips are rendered to static markup, which cuts them off from the React tree: the click
 * has to be delegated from the document, and the add-on state has to be read here, inside the
 * provider, and handed to the banner.
 */
export const useHDBannerCta = ({
    trackingName,
    period = HD_BANNER_PRESELECTED_PERIOD,
}: IUseHDBannerCtaParams): IHDBannerCtaState => {
    const { handlePurchaseAddonClick } = useNoTouchPurchaseContext();
    const { getAddon, getIsAddonUpgradable, isAddonsDataLoading } = useAddonsState();

    const ctaState = useMemo((): IHDBannerCtaState => {
        const addon = getAddon(AddOnType.HistoricalData);
        const isOwned = Boolean(addon);
        const canUpgradeAddon = isOwned && getIsAddonUpgradable(AddOnType.HistoricalData);

        return {
            canPurchaseAddon: addon === null,
            canUpgradeAddon,
            hasMaxedOutAddon: isOwned && !canUpgradeAddon,
            isLoading: isAddonsDataLoading,
        };
    }, [getAddon, getIsAddonUpgradable, isAddonsDataLoading]);

    useEffect(() => {
        const trackCtaEvent = (action: "click" | "hover"): void => {
            allTrackers.dispatchTrackEvent("Button", action, getHdBannerCtaTrackingName(trackingName, ctaState));
        };

        const onDocumentClick = (event: MouseEvent): void => {
            if (!(event.target instanceof Element) || !event.target.closest(HD_BANNER_CTA_SELECTOR)) {
                return;
            }

            trackCtaEvent("click");

            if (ctaState.canPurchaseAddon || ctaState.canUpgradeAddon) {
                handlePurchaseAddonClick(historyHookPreselect, period);
                return;
            }

            void openHooksModalV5("ContactUs", undefined, "Historical data from graph tooltip", "GetLoading");
        };

        const onDocumentHover = (event: MouseEvent): void => {
            if (!(event.target instanceof Element)) {
                return;
            }

            const cta = event.target.closest(HD_BANNER_CTA_SELECTOR);
            if (!cta) {
                return;
            }

            const relatedTarget = event.relatedTarget;
            if (relatedTarget instanceof Element && cta.contains(relatedTarget)) {
                return;
            }

            trackCtaEvent("hover");
        };

        const unsubscribeClick = subscribeHdBannerCtaClick(onDocumentClick);
        const unsubscribeHover = subscribeHdBannerCtaHover(onDocumentHover);

        return () => {
            unsubscribeClick();
            unsubscribeHover();
        };
    }, [ctaState, handlePurchaseAddonClick, period, trackingName]);

    return ctaState;
};
